import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EngagementModelConfigurator = () => {
  const [selectedModel, setSelectedModel] = useState('strategic-advisory');
  const [selectedCommitment, setSelectedCommitment] = useState('standard');

  const engagementModels = [
    {
      id: 'strategic-advisory',
      name: 'Strategic Advisory',
      icon: 'Target',
      description: 'Ongoing strategic guidance and board-level consultation',
      commitment: '2-3 days/month',
      price: 15000,
      deliverables: [
        'Monthly strategic planning sessions',
        'Quarterly board presentations',
        'Strategic roadmap development',
        'Executive stakeholder management'
      ]
    },
    {
      id: 'interim-leadership',
      name: 'Interim Leadership',
      icon: 'Briefcase',
      description: 'Full CDO responsibilities during transition or transformation',
      commitment: '3-4 days/week',
      price: 20000,
      deliverables: [
        'Complete CDO responsibilities',
        'Team leadership and development',
        'Vendor and partner management',
        'Transformation program oversight'
      ]
    },
    {
      id: 'transformation-guidance',
      name: 'Transformation Guidance',
      icon: 'TrendingUp',
      description: 'Intensive support for major data transformation initiatives',
      commitment: '4-5 days/week',
      price: 18000,
      deliverables: [
        'Transformation strategy and planning',
        'Change management leadership',
        'Technology evaluation and selection',
        'Implementation oversight'
      ]
    }
  ];

  const commitmentLevels = [
    {
      id: 'standard',
      name: 'Standard',
      multiplier: 1,
      description: 'Base engagement level'
    },
    {
      id: 'enhanced',
      name: 'Enhanced',
      multiplier: 1.3,
      description: '+30% time commitment'
    },
    {
      id: 'intensive',
      name: 'Intensive',
      multiplier: 1.6,
      description: '+60% time commitment'
    }
  ];

  const selectedModelData = engagementModels?.find(m => m?.id === selectedModel);
  const selectedCommitmentData = commitmentLevels?.find(c => c?.id === selectedCommitment);
  const calculatedPrice = Math.round(selectedModelData?.price * selectedCommitmentData?.multiplier);

  return (
    <div className="bg-white rounded-xl shadow-elevation p-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {engagementModels?.map((model) => (
          <div
            key={model?.id}
            onClick={() => setSelectedModel(model?.id)}
            className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg ${
              selectedModel === model?.id
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
          >
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
              selectedModel === model?.id ? 'bg-primary' : 'bg-surface'
            }`}>
              <Icon name={model?.icon} size={24} className={selectedModel === model?.id ? 'text-white' : 'text-primary'} />
            </div>
            <h3 className="text-xl font-bold mb-2">{model?.name}</h3>
            <p className="text-sm text-muted-foreground mb-3">{model?.description}</p>
            <div className="text-sm font-medium text-primary">{model?.commitment}</div>
          </div>
        ))}
      </div>
      <div className="mb-8">
        <h4 className="text-lg font-semibold mb-4">Time Commitment Level</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {commitmentLevels?.map((level) => (
            <div
              key={level?.id}
              onClick={() => setSelectedCommitment(level?.id)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                selectedCommitment === level?.id
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="font-semibold mb-1">{level?.name}</div>
              <div className="text-sm text-muted-foreground">{level?.description}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-surface rounded-xl p-6 border border-border">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h4 className="text-lg font-semibold mb-4">Selected Engagement</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Icon name="Check" size={16} className="text-primary" />
                <span className="font-medium">{selectedModelData?.name}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="Clock" size={16} className="text-primary" />
                <span className="text-muted-foreground">{selectedModelData?.commitment}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="TrendingUp" size={16} className="text-primary" />
                <span className="text-muted-foreground">{selectedCommitmentData?.name} commitment level</span>
              </div>
            </div>
            <div className="mt-6">
              <h5 className="font-semibold mb-3">Key Deliverables</h5>
              <div className="space-y-2">
                {selectedModelData?.deliverables?.map((deliverable, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="text-primary mt-1" />
                    <span className="text-sm text-muted-foreground">{deliverable}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div className="bg-white rounded-lg p-6 border border-border">
              <div className="text-sm text-muted-foreground mb-2">Monthly Investment</div>
              <div className="text-4xl font-bold text-primary mb-4">€{calculatedPrice?.toLocaleString()}</div>
              <div className="space-y-2 mb-6 text-sm text-muted-foreground">
                <div className="flex justify-between">
                  <span>Base engagement:</span>
                  <span>€{selectedModelData?.price?.toLocaleString()}</span>
                </div>
                {selectedCommitmentData?.multiplier > 1 && (
                  <div className="flex justify-between">
                    <span>Commitment adjustment:</span>
                    <span>+{Math.round((selectedCommitmentData?.multiplier - 1) * 100)}%</span>
                  </div>
                )}
                <div className="border-t border-border pt-2 flex justify-between font-semibold text-foreground">
                  <span>Total monthly:</span>
                  <span>€{calculatedPrice?.toLocaleString()}</span>
                </div>
              </div>
              <Button
                variant="default"
                fullWidth
                iconName="Calendar"
                iconPosition="left"
                className="bg-secondary hover:bg-secondary/90"
                onClick={() => window.location.href = '/contact-multi-channel-engagement-hub'}
              >
                Discuss This Engagement
              </Button>
            </div>
            <div className="mt-4 p-4 bg-primary/5 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-primary mt-1" />
                <p className="text-sm text-muted-foreground">
                  Minimum 6-month engagement. Pricing includes all strategic services, team development, and 24/7 consultation access.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementModelConfigurator;